"use client";

import Link from "next/link";
import { toneBadge } from "@/lib/statusTone";

/** Missing/!invalid API keys are fixable in Settings — link there directly. */
function isConfigError(message: string): boolean {
  return /not configured|api key|settings|unauthorized|401|invalid.*key/i.test(
    message
  );
}

/**
 * The foot of the thread: what would stop a message going out, what went
 * wrong with the last one, and the box to type the next one in. Enter sends
 * and Shift+Enter breaks a line; while a reply is streaming the Send button
 * becomes Stop.
 */
export default function Composer({
  inputId,
  workerName,
  input,
  onInputChange,
  onSend,
  onStop,
  sending,
  sendError,
  onDismissError,
  setupWarning,
}: {
  /** Optional id for focusing the composer from elsewhere. */
  inputId?: string;
  workerName: string;
  input: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  onStop: () => void;
  sending: boolean;
  sendError: string | null;
  onDismissError: () => void;
  /** Blocking setup problem shown before the user types (e.g. no API key). */
  setupWarning?: string | null;
}) {
  return (
    <>
      {setupWarning && !sendError && (
        <div className={`border-t px-4 py-2.5 text-xs leading-relaxed ${toneBadge.idle}`}>
          <span className="font-medium">Setup needed. </span>
          {setupWarning}{" "}
          <Link
            href="/settings"
            className="font-medium underline underline-offset-2 hover:text-white"
          >
            Open Settings
          </Link>
        </div>
      )}

      {sendError && (
        <div className={`border-t px-4 py-2.5 text-xs leading-relaxed ${toneBadge.danger}`}>
          <div className="flex items-start gap-3">
            <p className="min-w-0 flex-1">
              <span className="font-medium">
                Message not sent.{" "}
              </span>
              {sendError}
              {isConfigError(sendError) && (
                <>
                  {" "}
                  <Link
                    href="/settings"
                    className="font-medium underline underline-offset-2 hover:text-white"
                  >
                    Open Settings
                  </Link>{" "}
                  to add an API key.
                </>
              )}
            </p>
            <button
              type="button"
              onClick={onDismissError}
              className="shrink-0 opacity-80 hover:opacity-100"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      <div className="border-t border-zinc-800 p-3 sm:p-4">
        <div className="flex items-end gap-2 rounded-xl border border-zinc-700 bg-zinc-900 p-1.5 sm:p-2 focus-within:border-violet-500">
          <textarea
            id={inputId}
            aria-label={`Message ${workerName}`}
            value={input}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                onSend();
              }
            }}
            rows={Math.min(6, Math.max(1, input.split("\n").length))}
            placeholder={`Message ${workerName}…`}
            className="max-h-40 flex-1 resize-none bg-transparent px-2 py-1.5 text-base sm:text-sm text-zinc-100 outline-none placeholder:text-zinc-500"
          />
          {sending ? (
            <button
              onClick={onStop}
              aria-label="Stop generating"
              title="Stop generating"
              className="flex size-9 items-center justify-center rounded-lg border border-zinc-600 text-zinc-300 transition hover:border-zinc-500 hover:text-white active:scale-[0.96]"
            >
              <span className="block size-2.5 rounded-[2px] bg-current" />
            </button>
          ) : (
            <button
              onClick={onSend}
              disabled={!input.trim()}
              className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-violet-500 disabled:cursor-not-allowed disabled:opacity-40"
            >
              Send
            </button>
          )}
        </div>
      </div>
    </>
  );
}
